import { useRouter } from "next/router";
import { FC, useEffect, useState } from "react";
import styles from "./StockLinks.module.scss";
import { useAtomValue } from "jotai";
import { backgroundColorState, blackLighterState, stockReportAvailabilityState, tradingIdeaAvailabilityState, tabLinksColorState, themeState, userState, whiteDarkerState } from "@/lib/store";
import { isUsExchange } from "@/lib/exchanges";
import { Stock } from "@/models/stock";
import { Button, Chip, Menu, Typography, useMediaQuery } from "@mui/material";
import Link from "next/link";
import { MenuItem } from "@mui/material";
import { Box } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StockHeaderInfo from "../stock-header-info/StockHeaderInfo";
import { getStockActivistInvestors } from "@/controllers/institutional";
import { getAltDataForStock } from "@/controllers/alt-data";
import { getBondsForStock } from "@/controllers/bonds";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DesktopTabs from "./DesktopTabs";
import { BaseLink } from "./types";
import { useIdleEffect } from "@/lib/defer";
import { stockPath } from "@/lib/stock-url";

// Stock Reports flagged "New" inside this window after generation.
const STOCK_REPORT_NEW_WINDOW_DAYS = 10;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const getLinks = (
    pathname: string,
    isLiveFeed: string,
    stockType: number,
    exchangeCountry: string,
    exchangeSymbol: string,
    hasStockReport: boolean,
    stockReportIsNew: boolean,
    newOptionsUi = false,
    hasTradingIdea = false,
    hasActivists = false,
    hasAltData = false,
    hasBonds = false
): BaseLink[] => {
    let path: string;
    if (pathname.includes("/id/")) path = pathname.split("/id/[stock_id]")[1];
    else if (pathname.includes("/s/")) path = pathname.split("/s/[exchange]/[ticker]")[1];
    const links: BaseLink[] = [
        { href: "", text: "Overview", selected: true },
        // Stock Report tab — inserted right after Overview only when
        // the stock has a published Stock Report. The "New" flag
        // drives a small chip on the tab label so freshly-published
        // coverage stands out.
        ...(hasStockReport ? [{ href: "stock-report", text: "Stock Report", selected: false, isNew: stockReportIsNew, highlighted: true }] : []),
        // Trading Idea tab — same placement rule as Stock Report, and a
        // stock can carry both. Driven by a GATED probe, so `false` here
        // covers "no idea" and "not entitled to know" with one value.
        // It must stay an absence: an empty tab would tell anyone who
        // sees it that ORTEX has published a directional view on this
        // name, which is the information itself.
        ...(hasTradingIdea ? [{ href: "trading-idea", text: "Trading Idea", selected: false, highlighted: true }] : []),
        // Alt Data - conditional, because alternative-data coverage is uneven by
        // construction: a reactor series maps to utilities, an ABS trust to card
        // issuers, and most stocks match nothing. When a stock does have it, it
        // is the rarest thing on the strip, so it sits with the analysis tabs
        // and carries its own accent rather than the twentieth position.
        ...(hasAltData ? [{ href: "alt-data", text: "Alt Data", selected: false, accent: true }] : []),
        { href: "fundamentals", text: "Fundamentals", selected: false },
        { href: "events", text: "Events", selected: false },
        { href: "news", text: "News", selected: false },
        { href: "corporate-actions/all", text: "Corp Actions", selected: false },
        { href: "index-rebalance", text: "Index", selected: false },
        { href: "analysts", text: "Analysts", selected: false },
        { href: "eps", text: "EPS Momentum", selected: false },
        { href: "holders", text: "Holders", selected: false },
        // Activist Investors tab — only for stocks that actually have 13D/G
        // filings on the register (availability probe below). An absent tab
        // is the correct signal for the ~95% of stocks with no filings.
        ...(hasActivists ? [{ href: "activist-investors", text: "Activists", selected: false }] : []),
        { href: "dividend", text: "Dividend", selected: false },
        // Bonds - conditional: only companies with live bonds (their own or a consolidated
        // finance subsidiary's) get the tab. Most listed companies have none.
        ...(hasBonds ? [{ href: "bonds", text: "Bonds", selected: false }] : []),
        // Options Platform members (paid addon or the new-UI rollout
        // group) land on the new per-stock platform; everyone else keeps
        // the legacy options page. Old backends never send either flag,
        // so this defaults to legacy everywhere until rollout.
        { href: newOptionsUi ? "options-platform" : "options", text: "Options", selected: false },
        { href: "short-interest", text: "Shorts", selected: false },
        { href: "insiders", text: "Insiders", selected: false },
        { href: "peers", text: "Peers", selected: false },
        { href: "commodity-exposure", text: "Commodity Exp", selected: false },
        { href: "trading-signals", text: "Trading Signals", selected: false },
        { href: "stock-scores", text: "Stock Scores", selected: false },
        // { href: "cds", text: "CDS", selected: false },
    ];
    let index: number;
    if ((index = links.slice(1, links.length).findIndex((link) => path.includes(link.href))) >= 0) {
        links[0].selected = false;
        links[index + 1].selected = true;
    }
    // Match the allow-lists below on a tab's first path segment, not its whole href.
    // "Corp Actions" points at "corporate-actions/all", so an allow-list entry of
    // "corporate-actions" silently dropped the tab for every stockType 3 stock — the
    // entries were never updated when the href gained its sub-path.
    const tabRoot = (link: BaseLink) => link.href.split("/")[0];
    if (stockType === 3) {
        if (exchangeCountry === "US" && isUsExchange(exchangeSymbol)) {
            return links.filter((link) => ["", "events", "corporate-actions", "options", "options-platform", "short-interest", "trading-signals", "dividend"].includes(tabRoot(link)));
        } else {
            return links.filter((link) => ["", "events", "corporate-actions", "short-interest", "trading-signals", "dividend"].includes(tabRoot(link)));
        }
    } else if (stockType !== 1) return links.filter((link) => link.href === "trading-signals");
    // Options data is OPRA-licensed and US-only: the pages send any other listing to the overview.
    if (isLiveFeed === "EU" || !isUsExchange(exchangeSymbol)) return links.filter((link) => link.href !== "options" && link.href !== "options-platform");
    return links;
};

const MobileMenu = ({ currentTab, linksList, isEUStock, stock, pathname }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <div>
            <Button id="basic-button" aria-controls={open ? "basic-menu" : undefined} aria-haspopup="true" aria-expanded={open ? "true" : undefined} onClick={handleClick}>
                <Typography>{linksList[currentTab].text || "Overview"}</Typography> <ExpandMoreIcon />
            </Button>
            <Menu id="basic-menu" anchorEl={anchorEl} open={open} onClose={handleClose} sx={{ maxHeight: "90%" }}>
                {linksList.map((link) =>
                    isEUStock && link.text === "Options" ? null : (
                        <MenuItem
                            value={link.text}
                            sx={{
                                "& a": {
                                    all: "unset",
                                },
                            }}
                            onClick={() => setAnchorEl(null)}
                            selected={link.text === linksList[currentTab].text}
                            key={link.text}
                        >
                            <Link href={pathname.includes("/s/") ? stockPath(stock, link.href) : `/id/${stock.stock_id}/${link.href}`} className={styles.selectLink} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                {link.accent && <AutoAwesomeIcon sx={{ fontSize: 14, color: "#26a69a" }} />}
                                <span style={link.highlighted || link.accent ? { fontWeight: 700 } : undefined}>{link.text}</span>
                                {link.isNew && <Chip label="New" size="small" color="primary" sx={{ height: 16, fontSize: 9, fontWeight: 600, "& .MuiChip-label": { px: 0.5, lineHeight: 1 } }} />}
                            </Link>
                        </MenuItem>
                    )
                )}
            </Menu>
        </div>
    );
};

const StockLinks: FC<{ stock: Stock }> = ({ stock }) => {
    const { pathname } = useRouter();

    const user = useAtomValue(userState);
    const theme = useAtomValue(themeState);
    const whiteDarker = useAtomValue(whiteDarkerState);
    const blackLighter = useAtomValue(blackLighterState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const tabColor = useAtomValue(tabLinksColorState);

    const [currentTab, setCurrentTab] = useState(0);

    const isMobile = useMediaQuery("(max-width: 768px)");

    const isUSStock = stock.isLiveFeed === "NA";
    const isEUStock = stock.isLiveFeed === "EU";

    // Stock-report availability is populated by RootLayout when a
    // stock-page mounts. ``null`` while pending → tab simply doesn't
    // render yet; once resolved the tab appears (or not).
    const stockReportAvailability = useAtomValue(stockReportAvailabilityState);
    const hasStockReport = !!stockReportAvailability?.exists;
    const stockReportIsNew = (() => {
        if (!stockReportAvailability?.exists || !stockReportAvailability.generatedAt) return false;
        const ageMs = Date.now() - new Date(stockReportAvailability.generatedAt).getTime();
        return ageMs >= 0 && ageMs < STOCK_REPORT_NEW_WINDOW_DAYS * MS_PER_DAY;
    })();

    const newOptionsUi = !!(user?.options_platform_subscriber || user?.new_options_ui);
    const tradingIdeaAvailability = useAtomValue(tradingIdeaAvailabilityState);
    const hasTradingIdea = !!tradingIdeaAvailability?.exists;

    // Activist Investors availability — one light probe per stock against the
    // SWR-cached register endpoint; the tab appears only when the stock has
    // 13D/G filings. Starts false so the tab pops in rather than flickering out.
    //
    // The probe runs from idle (``useIdleEffect``): it decides whether a tab
    // exists and nothing above the fold waits on the answer, so it has no
    // business competing with the page's own data for the connection. Clearing
    // the previous stock's answer stays eager - it has to land in the same
    // commit as the navigation, or the old stock's tab sits in the strip under
    // the new stock's name.
    const [hasActivists, setHasActivists] = useState(false);
    useEffect(() => {
        setHasActivists(false);
    }, [stock?.tradingitem_id]);
    // No AbortController: `installGetDeduplication` will not share a GET that
    // carries a signal, so passing one opts this probe out of the machinery
    // that collapses duplicate calls. A `cancelled` flag ignores a late answer
    // just as well and lets a second mount join the first request.
    useIdleEffect(() => {
        if (!stock?.tradingitem_id) return;
        let cancelled = false;
        getStockActivistInvestors({ page_size: 1, page: 1, data: { tradingitem_id: stock.tradingitem_id } })
            .then((data) => !cancelled && setHasActivists(!!data?.rows?.length))
            .catch(() => !cancelled && setHasActivists(false));
        return () => {
            cancelled = true;
        };
    }, [stock?.tradingitem_id]);

    // Alt Data availability — same shape as the Activists probe above, deferred
    // and cleared eagerly for the same reasons. The endpoint answers "is there
    // anything for this instrument", so one call decides the tab.
    const [hasAltData, setHasAltData] = useState(false);
    useEffect(() => {
        setHasAltData(false);
    }, [stock?.tradingitem_id]);
    useIdleEffect(() => {
        // Two conditions, and the entitlement is checked first so an unentitled
        // user never issues the probe at all: the API would 403 it anyway (the
        // views require PRODUCT_ALT_DATA), and a guaranteed-failing request per
        // stock page view is pure noise in the logs.
        if (!user?.alt_data) return;
        if (!stock?.tradingitem_id) return;
        // Signal-free for the same dedupe reason as the probe above.
        let cancelled = false;
        getAltDataForStock(stock.tradingitem_id)
            .then((data) => !cancelled && setHasAltData(!!data?.hasData))
            .catch(() => !cancelled && setHasAltData(false));
        return () => {
            cancelled = true;
        };
    }, [stock?.tradingitem_id, user?.alt_data]);

    // Bonds availability - same shape as the Alt Data probe above. The bond API is gated on the
    // same Advanced-tier mask as Alt Data, so `user.alt_data` is the entitlement check that keeps
    // an unentitled user from issuing a probe the API would refuse.
    const [hasBonds, setHasBonds] = useState(false);
    useEffect(() => {
        setHasBonds(false);
    }, [stock?.tradingitem_id]);
    useIdleEffect(() => {
        if (!user?.alt_data) return;
        if (!stock?.tradingitem_id) return;
        let cancelled = false;
        getBondsForStock(stock.tradingitem_id)
            .then((data) => !cancelled && setHasBonds(!!data?.hasData))
            // The probe rejects on any non-200 (BondsHttpError): no answer, no tab. Only
            // alt_data users probe at all, so a refusal here is an outage, not a paywall.
            .catch(() => !cancelled && setHasBonds(false));
        return () => {
            cancelled = true;
        };
    }, [stock?.tradingitem_id, user?.alt_data]);

    const linksList = getLinks(pathname, stock.isLiveFeed, stock.type, stock.exchangeCountry, stock.exchangesymbol, hasStockReport, stockReportIsNew, newOptionsUi, hasTradingIdea, hasActivists, hasAltData, hasBonds);

    // Applied here rather than while rendering: the strip measures the tabs it is
    // given, and a tab that renders as null would still be counted as taking width.
    const desktopLinks = linksList.filter((link) => !(isEUStock && link.text === "Options"));

    const tabHref = (link: BaseLink) => (pathname.includes("/id/") ? `/id/${stock.stock_id}/${link.href}` : stockPath(stock, link.href));

    const handleChangeTab = (event, newValue) => {
        newValue && setCurrentTab(newValue);
    };

    // Keyed on the selected index, not on linksList: getLinks builds a new array on every render, so the
    // effect ran after every render and set the tab again. Setting a state to the value it already has can
    // still cost React one more render of the component before it bails out, and each of those renders ran
    // the effect again: on a stock page the strip (with the stock header inside it on a phone) re-rendered
    // hundreds of times in a row, 30-150 ms each on a throttled phone (ortex-gui#1349).
    const selectedIndex = linksList.findIndex((link) => link.selected);
    useEffect(() => {
        setCurrentTab(selectedIndex !== -1 ? selectedIndex : 0);
    }, [selectedIndex]);

    return (
        <div className={styles.StockLinks} style={{ visibility: user.email ? "visible" : "hidden" }}>
            <style>
                {`
                .MuiTabScrollButton-root {
                    overflow: hidden;
                    transition: width 0.5s;
                    width: 28px;
                }

                .MuiTabScrollButton-root.Mui-disabled {
                    width: 0px;
                }
                
                .${styles.stockTabs} a:hover {
                    background-color: ${theme === "dark" ? blackLighter : whiteDarker};
                }
            `}
            </style>
            {isMobile ? (
                <Box sx={{ display: "flex", alignItems: "center", mt: "5.5px", mb: "6px", mr: 1 }}>
                    <StockHeaderInfo stock={stock} />
                    <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
                        <MobileMenu currentTab={currentTab} linksList={linksList} isEUStock={isEUStock} stock={stock} pathname={pathname} />
                    </Box>
                </Box>
            ) : (
                <DesktopTabs links={desktopLinks} tabColor={tabColor} hrefFor={tabHref} />
            )}
        </div>
    );
};

export default StockLinks;
